import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  applySpec,
  compose,
  cond,
  equals,
  has,
  map,
  pathEq,
  prop,
  reduce,
  replace,
  test,
  omit,
} from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import Divider from '@material-ui/core/Divider';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import RevealIcon from '@material-ui/icons/VisibilityOffOutlined';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { FormField, TextField, AlertHelpDialog } from '@iris-platform/iris-react-ui';
import Button from '@material-ui/core/Button';
import {
  withApollo,
  Query,
} from 'react-apollo';
import { HasPermission } from '@iris-platform/protektor';
import CreateChildApplication from '../../CreateApplication';
import {
  stylesToPropType,
  copyToClipboard,
  hideAfterAnimate,
  capitalize,
} from '../../../utils';
import {
  GET_CLIENT_ID,
  GET_CLIENT_SECRET,
  GET_PRIVATE_KEY,
  ROTATE_ALL,
  ROTATE_KEYS,
  GET_USER,
  GET_TEAM,
} from '../queries';
import applicationPropType from '../applicationPropType';
import { PROTEKTOR_VIEW, PROTEKTOR_DELETE, PROTEKTOR_CREATE } from '../../../utils/protektorConstants';
import Helpfile from '../helpfile.json';

const REVEAL_ANIMATION_DURATION_MS = 500;
const SECRET_TTL_MS = 60000;

const copyButtonStyles = () => ({
  root: {
    cursor: 'pointer',
  },
  animateClick: {
    animation: 'bounce 0.35s ease-out',
  },
  '@keyframes bounce': {
    '0%': {
      transform: 'scale(1, 1)',
    },
    '25%': {
      transform: 'scale(1.14, 1.14)',
    },
    '50%': {
      transform: 'scale(1.2, 1.2)',
    },
    '75%': {
      transform: 'scale(1.07, 1.07)',
    },
    '100%': {
      transform: 'scale(1, 1)',
    },
  },
});

const UnstyledCopyButton = ({
  className,
  classes,
  value,
}) => (
  <CopyIcon
    className={`${className} ${classes.root}`}
    onClick={(e) => {
      let icon = e.target;

      while (icon.tagName.toLowerCase() !== 'svg') icon = icon.parentElement;

      window.requestAnimationFrame(() => {
        icon.classList.remove(classes.animateClick);

        setImmediate(() => {
          window.requestAnimationFrame(() => {
            icon.classList.add(classes.animateClick);
          });
        });
      });


      copyToClipboard(value);
    }}
  />
);

UnstyledCopyButton.propTypes = {
  className: PropTypes.string,
  classes: stylesToPropType(copyButtonStyles()).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
};

UnstyledCopyButton.defaultProps = {
  className: '',
};

const CopyButton = withStyles(copyButtonStyles)(UnstyledCopyButton);


const revealButtonStyles = () => ({
  root: {
    cursor: 'pointer',
  },
  animateClick: {
    animation: `flush ${REVEAL_ANIMATION_DURATION_MS / 1000}s ease-out`,
  },
  '@keyframes flush': {
    from: {
      transform: 'rotate(0deg) scale(1, 1)',
      opacity: 1,
    },
    to: {
      transform: 'rotate(360deg) scale(0, 0)',
      opacity: 0,
    },
  },
});

const UnstyledRevealButton = ({
  className,
  classes,
  onClick,
}) => (
  <RevealIcon
    className={`${className} ${classes.root}`}
    onClick={(e) => {
      let icon = e.target;

      while (icon.tagName.toLowerCase() !== 'svg') icon = icon.parentElement;

      window.requestAnimationFrame(() => {
        icon.classList.remove(classes.animateClick);
        hideAfterAnimate(icon);

        setImmediate(() => {
          window.requestAnimationFrame(() => {
            icon.classList.add(classes.animateClick);
          });
        });
      });


      onClick();
    }}
  />
);

UnstyledRevealButton.propTypes = {
  className: PropTypes.string,
  classes: stylesToPropType(revealButtonStyles()).isRequired,
  onClick: PropTypes.func,
};

UnstyledRevealButton.defaultProps = {
  className: '',
  onClick: () => {},
};

const RevealButton = withStyles(revealButtonStyles)(UnstyledRevealButton);

const isRevealed = test(/[^*\n]/);

const getQueryForField = cond([
  [equals('clientID'), () => GET_CLIENT_ID],
  [equals('clientSecret'), () => GET_CLIENT_SECRET],
  [equals('privateKey'), () => GET_PRIVATE_KEY],
]);

const redact = replace(/./g, '*');

const styles = () => ({
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  disabledField: {
    cursor: 'default',
    '& *': {
      color: '#606060',
    },
  },
  endAdornment: {
    marginRight: '0.5em',
  },
  expiring: {
    position: 'relative',
    zIndex: 0,
    '& svg.timer': {
      zIndex: 0,
      position: 'absolute',
      top: '-0.63em',
      left: '-0.63em',
      height: '2.9em',
      width: '2.9em',
      transform: 'rotate(-90deg)',
      '& circle': {
        stroke: '#d2d2d28f',
        strokeWidth: '40',
        fill: 'none',
        strokeDasharray: '126 252',
        animation: `wipe ${SECRET_TTL_MS / 1000}s linear`,
      },
    },
    '& svg:not(.timer)': {
      zIndex: 1,
      position: 'sticky',
    },
  },
  divider: {
    margin: '2em 0',
  },
  textField: {
    height: 'auto',
    paddingLeft: '0.5em',
    '&::before, &:hover::before': {
      display: 'none !important',
    },
  },
  button: {
    borderRadius: '0.125em',
    marginTop: '1em',
    marginRight: '0em',
    width: '8em',
  },
  icon: {
    // margin: theme.spacing.unit,
    fontSize: 14,
    cursor: 'pointer',
  },
  iconbutton: {
    width: '18px',
    height: '18px',
  },
  childBtn: {
    marginRight: '0em',
    width: '13em',
    margin: '0px',
    marginTop: '1em',
  },
  resetContainer: {
    marginLeft: '12px',
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& [class^="FormField"]': {
      flexGrow: '0.95',
      '& [class^="MuiInput"]': {
        margin: '6px',
      },
    },
  },
  warning: {
    color: '#f50057',
  },
  '@keyframes wipe': {
    '0%': {
      strokeDashoffset: 0,
    },
    '100%': {
      strokeDashoffset: -126,
    },
  },
});

class UnstyledGeneralInfoTab extends Component {
  constructor(props) {
    super(props);

    const { application } = props;
    this.state = {
      resetEnabled: false,
      keyResetEnabled: false,
      isModalOpen: false,
      application: {
        ...application,
        authString: redact(`${application.clientID}:${application.clientSecret}`),
      },
      open: false,
      dialogTitle: '',
      dialogContent: '',
    };
  }

  handleHelpOpen = (title, content) => {
    this.setState({
      open: true,
      dialogTitle: title,
      dialogContent: content,
    });
  };

  handleHelpClose = () => {
    this.setState({
      open: false,
      dialogTitle: '',
      dialogContent: '',
    });
  };

  reveal = async (...fields) => {
    const start = new Date();
    const { client } = this.props;
    const { application: { applicationID } } = this.state;

    const queries = map(
      async (field) => {
        const query = getQueryForField(field);
        const { data } = await client.query({
          query,
          variables: { applicationID },
          fetchPolicy: 'network-only',
        });
        const value = data[`application${capitalize(field)}`];

        return { [field]: value };
      },
      fields,
    );
    const results = await Promise.all(queries);
    const update = reduce((result, accum) => ({ ...accum, ...result }), {}, results);

    if (has('clientSecret', update) && has('clientID', update)) {
      update.authString = btoa(`${
        prop('clientID', update)
      }:${
        prop('clientSecret', update)
      }`);
    }

    const redaction = map(redact, omit(['clientID'], update));

    const queryDuration = (new Date()) - start;
    const delay = Math.max(0, REVEAL_ANIMATION_DURATION_MS - queryDuration);
    setTimeout(() => this.setState(state => ({
      application: {
        ...state.application,
        ...update,
      },
    })), delay);
    setTimeout(() => this.setState(state => ({
      application: {
        ...state.application,
        ...redaction,
      },
    })), SECRET_TTL_MS);
  };

  reset = async (keysOnly = false) => {
    const { client, enqueueSnackbar } = this.props;
    const { application: { applicationID } } = this.state;

    const mutation = keysOnly ? ROTATE_KEYS : ROTATE_ALL;

    try {
      const {
        data: {
          rotateApplicationCredentials,
          rotateApplicationKeys,
        },
      } = await client.mutate({
        mutation,
        variables: { applicationID },
      });

      enqueueSnackbar('Successfully reset credentials', { variant: 'success' });

      this.setState(state => ({
        application: {
          ...state.application,
          ...rotateApplicationCredentials,
          ...rotateApplicationKeys,
          authString: redact(
            `${rotateApplicationCredentials.clientID}:${rotateApplicationCredentials.clientSecret}`,
          ),
        },
      }));
    } catch (err) {
      enqueueSnackbar('Error resetting credentials', { variant: 'error' });
    }
  }

  handleSaveChildApp = (application) => {
    this.setState({ isModalOpen: false, application });
  }

  handleModalClose = () => {
    this.setState({ isModalOpen: false });
  }

  onClickCreateChild = () => {
    this.setState({ isModalOpen: true });
  }

  render() {
    const { classes, role, createdBy } = this.props;
    const {
      resetEnabled,
      keyResetEnabled,
      application: {
        appType,
        domain,
        authString,
        federationType,
        clientID,
        clientSecret,
        publicKey,
        privateKey,
      },
      isModalOpen,
      open,
      dialogTitle,
      dialogContent,
    } = this.state;
    const help = Helpfile.general;

    return (
      <Fragment>
        <form className={classes.root}>
          <div className={classes.createAppModal}>
            <CreateChildApplication
              open={isModalOpen}
              handleClose={this.handleModalClose}
              application={this.state.application}
              onSave={this.handleSaveChildApp}
            />
          </div>
          <HasPermission
            to={PROTEKTOR_VIEW}
            access="Application_General_Tab"
            forRole={role}
          >
            <div>
              <Typography
                variant="subheading"
                gutterBottom
              >
                Domain
                <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.domain.title, help.domain.content)}>
                  <HelpOutlineIcon className={classes.icon} />
                </IconButton>
              </Typography>
              <FormField
                // label="Domain"
                inputClasses={{ input: classes.disabledField }}
                value={domain}
                endAdornment={(
                  <CopyButton
                    className={classes.endAdornment}
                    value={domain}
                  />
                )}
              />
              <br />
              <br />
              <Query
                query={GET_USER}
                notifyOnNetworkStatusChange
                variables={{ userID: createdBy }}
              >
                {({ data, loading, error }) => {
                  if (loading) return null;
                  if (error) return `Error!: ${error}`;

                  if (data.user !== null && data.user !== undefined && data.user !== {}) {
                    return (
                      <div>
                        <Typography
                          variant="subheading"
                          gutterBottom
                        >
                          Created By
                          <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.createdBy.title, help.createdBy.content)}>
                            <HelpOutlineIcon className={classes.icon} />
                          </IconButton>
                        </Typography>
                        <FormField
                          inputClasses={{ input: classes.disabledField }}
                          value={data.user.auminfo.user_name}
                          endAdornment={(
                            <CopyButton
                              className={classes.endAdornment}
                              value={domain}
                            />
                          )}
                        />
                        <br />
                        <FormField
                          inputClasses={{ input: classes.disabledField }}
                          value={data.user.auminfo.email}
                          endAdornment={(
                            <CopyButton
                              className={classes.endAdornment}
                              value={domain}
                            />
                          )}
                        />
                      </div>
                    );
                  }
                  return <div />;
                }}
              </Query>
              <Query
                query={GET_TEAM}
                notifyOnNetworkStatusChange
                variables={{ id: createdBy }}
              >
                {({ data, loading, error }) => {
                  if (loading) return null;
                  if (error) return `Error!: ${error}`;

                  if (data.team !== null && data.team !== undefined && data.team !== {}) {
                    return (
                      <div>
                        <Typography
                          variant="subheading"
                          gutterBottom
                        >
                          Created By
                          <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.createdBy.title, help.createdBy.content)}>
                            <HelpOutlineIcon className={classes.icon} />
                          </IconButton>
                        </Typography>
                        <FormField
                          inputClasses={{ input: classes.disabledField }}
                          value={data.team.display_name}
                          endAdornment={(
                            <CopyButton
                              className={classes.endAdornment}
                              value={domain}
                            />
                          )}
                        />
                      </div>
                    );
                  }
                  return <div />;
                }}
              </Query>
              <br />
              <br />
              <Typography
                variant="subheading"
                gutterBottom
              >
                Federation Type
                <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.federationType.title, help.federationType.content)}>
                  <HelpOutlineIcon className={classes.icon} />
                </IconButton>
              </Typography>
              <FormField
                // label="Federation Type"
                inputClasses={{ input: classes.disabledField }}
                value={federationType}
                endAdornment={(
                  <CopyButton
                    className={classes.endAdornment}
                    value={federationType}
                  />
                )}
              />
              <br />
              <br />
              <Typography
                variant="subheading"
                gutterBottom
              >
                Client ID
                <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.clientId.title, help.clientId.content)}>
                  <HelpOutlineIcon className={classes.icon} />
                </IconButton>
              </Typography>
              <FormField
                // label="Client ID"
                inputClasses={{ input: classes.disabledField }}
                value={clientID}
                endAdornment={(
                  <CopyButton
                    className={classes.endAdornment}
                    value={clientID}
                  />
                )}
              />
              <br />
              <br />
              <Typography
                variant="subheading"
                gutterBottom
              >
                Client Secret
                <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.clientSecret.title, help.clientSecret.content)}>
                  <HelpOutlineIcon className={classes.icon} />
                </IconButton>
              </Typography>
              <FormField
                // label="Client Secret"
                inputClasses={{
                  input: classes.disabledFieldng,
                }}
                value={clientSecret}
                endAdornment={isRevealed(clientSecret) ? (
                  <span className={classes.expiring}>
                    <svg className="timer" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="20" />
                    </svg>
                    <CopyButton
                      className={classes.endAdornment}
                      value={clientSecret}
                    />
                  </span>
                ) : (
                  <RevealButton
                    className={classes.endAdornment}
                    onClick={() => this.reveal('clientSecret')}
                  />
                )}
              />
              <br />
              <br />
              <Typography
                variant="subheading"
                gutterBottom
              >
                Auth String
                <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.authString.title, help.authString.content)}>
                  <HelpOutlineIcon className={classes.icon} />
                </IconButton>
              </Typography>
              <FormField
                // label="Auth String"
                inputClasses={{
                  input: classes.disabledField,
                }}
                value={authString}
                endAdornment={isRevealed(authString) ? (
                  <span className={classes.expiring}>
                    <svg className="timer" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="20" />
                    </svg>
                    <CopyButton
                      className={classes.endAdornment}
                      value={authString}
                    />
                  </span>
                ) : (
                  <RevealButton
                    className={classes.endAdornment}
                    onClick={() => this.reveal('clientID', 'clientSecret')}
                  />
                )}
              />
            </div>
          </HasPermission>

          <Divider
            variant="middle"
            classes={{ root: classes.divider }}
          />

          <HasPermission
            to={PROTEKTOR_VIEW}
            access="Application_JWT_Keys"
            forRole={role}
          >
            <React.Fragment>
              <Typography
                component="h3"
                variant="subheading"
                gutterBottom
              >
                JWT Keys
                <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.jwtKeys.title, help.jwtKeys.content)}>
                  <HelpOutlineIcon className={classes.icon} />
                </IconButton>
              </Typography>
              <br />
              <TextField
                label="Public Key"
                value={publicKey}
                className={classes.textField}
                multiline
                disabled
                InputProps={{
                  className: `${classes.disabledField} ${classes.textField}`,
                  endAdornment: (
                    <CopyButton
                      className={classes.endAdornment}
                      value={publicKey}
                    />
                  ),
                }}
              />
              <br />
              <TextField
                label="Private Key"
                value={privateKey}
                className={classes.textField}
                multiline
                disabled
                InputProps={{
                  className: `${classes.disabledField} ${classes.textField}`,
                  endAdornment: isRevealed(privateKey) ? (
                    <span className={classes.expiring}>
                      <svg className="timer" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="20" />
                      </svg>
                      <CopyButton
                        className={classes.endAdornment}
                        value={privateKey}
                      />
                    </span>
                  ) : (
                    <RevealButton
                      className={classes.endAdornment}
                      onClick={() => this.reveal('privateKey')}
                    />
                  ),
                }}
              />
              <Divider
                variant="middle"
                classes={{ root: classes.divider }}
              />
            </React.Fragment>
          </HasPermission>

          <HasPermission
            to={PROTEKTOR_DELETE}
            access="Application_General_Tab"
            forRole={role}
          >
            <React.Fragment>
              <Typography
                component="h3"
                variant="subheading"
                classes={{ root: classes.warning }}
                gutterBottom
              >
                Credential Management
                <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.credentialManagement.title, help.credentialManagement.content)}>
                  <HelpOutlineIcon className={classes.icon} />
                </IconButton>
              </Typography>
              <div className={classes.resetContainer}>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!resetEnabled}
                  classes={{ root: classes.button }}
                  onClick={() => this.reset()}
                >
                    Reset
                </Button>
                <FormField
                  type="text"
                  placeholder={`enter the full domain name (${domain}) to enable reset`}
                  onChange={compose(
                    this.setState.bind(this),
                    applySpec({ resetEnabled: pathEq(['target', 'value'], domain) }),
                  )}
                />
                <FormHelperText style={{ width: '100%' }}>
                  Reset the client ID, client secret, public key, and private key for this application
                </FormHelperText>
              </div>
            </React.Fragment>
          </HasPermission>
          <br />
          <HasPermission
            to={PROTEKTOR_DELETE}
            access="Application_JWT_Keys"
            forRole={role}
          >
            <div className={classes.resetContainer}>
              <Button
                variant="contained"
                color="secondary"
                disabled={!keyResetEnabled}
                classes={{ root: classes.button }}
                onClick={() => this.reset(true)}
              >
                  Reset Keys
              </Button>


              <FormField
                type="text"
                placeholder={`enter the full domain name (${domain}) to enable key reset`}
                onChange={compose(
                  this.setState.bind(this),
                  applySpec({ keyResetEnabled: pathEq(['target', 'value'], domain) }),
                )}
              />
              <FormHelperText style={{ width: '100%' }}>
                Reset the public and private keys only
              </FormHelperText>
            </div>
          </HasPermission>
          {appType === 'master'
          && (
            <HasPermission
              to={PROTEKTOR_CREATE}
              access="Application_Child_App"
              forRole={role}
            >
              <Button
                color="primary"
                variant="contained"
                classes={{ root: classes.childBtn }}
                onClick={this.onClickCreateChild}
              >
                Create Child App
              </Button>
            </HasPermission>
          )
          }
        </form>
        <AlertHelpDialog
          handleClose={this.handleHelpClose}
          open={open}
          dialogTitle={dialogTitle}
          dialogContent={dialogContent}
        />
      </Fragment>
    );
  }
}

UnstyledGeneralInfoTab.propTypes = {
  application: applicationPropType.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  createdBy: PropTypes.string.isRequired,
};

const CredentialsTab = withStyles(styles)(UnstyledGeneralInfoTab);

export default withSnackbar(withApollo(CredentialsTab));
